import { useState, useEffect, useCallback } from 'react';
import { reportsApi } from '../lib/reports-api';
import type {
  DashboardMetrics,
  JobReportMetrics,
  RecruiterMetrics,
  RecruiterDetailMetrics,
  PlatformMetrics,
  ExportRequest,
  ExportRecord,
} from '@recruitai/shared';

interface UseQueryResult<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useDashboardMetrics(startDate: string, endDate: string): UseQueryResult<DashboardMetrics> {
  const [data, setData] = useState<DashboardMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await reportsApi.fetchDashboardMetrics(startDate, endDate);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch dashboard'));
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, error, refetch: fetch };
}

export function useJobReport(jobId: string, startDate: string, endDate: string): UseQueryResult<JobReportMetrics> {
  const [data, setData] = useState<JobReportMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!jobId) return;
    try {
      setIsLoading(true);
      setError(null);
      const result = await reportsApi.fetchJobReport(jobId, startDate, endDate);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch job report'));
    } finally {
      setIsLoading(false);
    }
  }, [jobId, startDate, endDate]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, error, refetch: fetch };
}

export function useRecruitersReport(startDate: string, endDate: string): UseQueryResult<RecruiterMetrics[]> {
  const [data, setData] = useState<RecruiterMetrics[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await reportsApi.fetchRecruitersReport(startDate, endDate);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch recruiters report'));
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, error, refetch: fetch };
}

export function useRecruiterDetail(userId: string, startDate: string, endDate: string): UseQueryResult<RecruiterDetailMetrics> {
  const [data, setData] = useState<RecruiterDetailMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!userId) return;
    try {
      setIsLoading(true);
      setError(null);
      const result = await reportsApi.fetchRecruiterDetail(userId, startDate, endDate);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch recruiter detail'));
    } finally {
      setIsLoading(false);
    }
  }, [userId, startDate, endDate]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, error, refetch: fetch };
}

export function usePlatformReport(startDate: string, endDate: string): UseQueryResult<PlatformMetrics> {
  const [data, setData] = useState<PlatformMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await reportsApi.fetchPlatformReport(startDate, endDate);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch platform report'));
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, isLoading, error, refetch: fetch };
}

export function useExportReport() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const exportReport = useCallback(async (request: ExportRequest): Promise<ExportRecord | null> => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await reportsApi.requestExport(request);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Export failed'));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { exportReport, isLoading, error };
}
